import tokensCss from '@spezzial/brand-ui/tokens.css?inline';
import utilitiesCss from '@spezzial/brand-ui/utilities.css?inline';
import { readConsent, subscribeConsent, openCookieSettings } from '@spezzial/consent';
import componentCss from './styles/component.css?inline';
import { loadGoogleMaps } from './map/loader.ts';
import { SPEZZIAL_MAP_STYLE } from './map/map-style.ts';
import { createMarker } from './map/marker.ts';
import { InfoPanel } from './panel/info-panel.ts';
import type { Partner } from './types.ts';

const TEMPLATE_STYLES = `${tokensCss}\n${utilitiesCss}\n${componentCss}`;

export class SpezzialPartnerMap extends HTMLElement {
  static readonly observedAttributes = [
    'api-key',
    'center-lat',
    'center-lng',
    'zoom',
    'height',
    'data-src',
  ] as const;

  #shadow!: ShadowRoot;
  #root!: HTMLDivElement;
  #content!: HTMLDivElement;
  #panel!: InfoPanel;
  #unsubscribe?: () => void;
  #consentGranted = false;
  #map?: google.maps.Map;
  #markers: google.maps.Marker[] = [];

  get apiKey(): string {
    return this.getAttribute('api-key') ?? '';
  }

  get centerLat(): number {
    return this.#numAttr('center-lat', 51.1657);
  }

  get centerLng(): number {
    return this.#numAttr('center-lng', 10.4515);
  }

  get zoom(): number {
    return this.#numAttr('zoom', 6);
  }

  get heightCss(): string | null {
    return this.getAttribute('height');
  }

  get dataSrc(): string | null {
    return this.getAttribute('data-src');
  }

  #numAttr(name: string, fallback: number): number {
    const raw = this.getAttribute(name);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }

  connectedCallback() {
    if (!this.#shadow) {
      this.#shadow = this.attachShadow({ mode: 'open' });
      const style = document.createElement('style');
      style.textContent = TEMPLATE_STYLES;
      this.#shadow.appendChild(style);

      this.#root = document.createElement('div');
      this.#root.className = 'root';
      this.#shadow.appendChild(this.#root);

      this.#content = document.createElement('div');
      this.#content.className = 'content';
      this.#root.appendChild(this.#content);

      this.#panel = new InfoPanel(this.#shadow);
      this.#panel.mount(this.#root);
    }
    if (this.heightCss) {
      this.style.setProperty('--spezzial-map-h', this.heightCss);
    }

    this.#consentGranted = readConsent();
    this.#render();
    this.#unsubscribe = subscribeConsent((granted) => {
      if (granted === this.#consentGranted) return;
      this.#consentGranted = granted;
      this.#render();
    });
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = undefined;
    this.#panel?.destroy();
  }

  attributeChangedCallback(name: string, _old: string | null, _next: string | null) {
    if (!this.isConnected) return;
    if (name === 'height' && this.heightCss) {
      this.style.setProperty('--spezzial-map-h', this.heightCss);
    }
  }

  #render() {
    if (this.#consentGranted) {
      void this.#mountMap();
    } else {
      this.#tearDownMap();
      this.#renderConsentGate();
    }
  }

  async #mountMap() {
    this.#root.setAttribute('data-state', 'ready');
    this.#content.innerHTML = `
      <div class="map" aria-label="Karte der Service-Partner"></div>
      <div class="status" role="status" aria-live="polite">Karte wird geladen…</div>
    `;
    const container = this.#content.querySelector<HTMLDivElement>('.map');
    const status = this.#content.querySelector<HTMLDivElement>('.status');
    if (!container || !status) return;

    if (!this.apiKey) {
      status.textContent = 'Fehler: kein api-key gesetzt.';
      return;
    }

    try {
      await loadGoogleMaps(this.apiKey);
      this.#map = new google.maps.Map(container, {
        center: { lat: this.centerLat, lng: this.centerLng },
        zoom: this.zoom,
        styles: SPEZZIAL_MAP_STYLE,
        disableDefaultUI: false,
        clickableIcons: false,
        backgroundColor: '#ffffff',
        gestureHandling: 'cooperative',
      });
      status.remove();
      const partners = await this.#loadPartners();
      this.#markers = partners.map((p) =>
        createMarker(this.#map!, p, (partner) => this.#onMarkerClick(partner)),
      );
      this.#fitBoundsToMarkers();
    } catch (err) {
      console.error('[spezzial-partner-map] Maps load failed:', err);
      status.textContent = 'Karte konnte nicht geladen werden.';
    }
  }

  #tearDownMap() {
    for (const m of this.#markers) m.setMap(null);
    this.#markers = [];
    this.#map = undefined;
    this.#panel?.close();
  }

  async #loadPartners(): Promise<Partner[]> {
    if (this.dataSrc) {
      const res = await fetch(this.dataSrc, { credentials: 'omit' });
      if (!res.ok) throw new Error(`Partner fetch failed: ${res.status}`);
      return (await res.json()) as Partner[];
    }
    const mod = await import('./data/partners.json');
    return mod.default as Partner[];
  }

  #fitBoundsToMarkers() {
    if (!this.#map || this.#markers.length === 0) return;
    if (this.#markers.length === 1) return;
    const bounds = new google.maps.LatLngBounds();
    for (const m of this.#markers) {
      const pos = m.getPosition();
      if (pos) bounds.extend(pos);
    }
    this.#map.fitBounds(bounds, 64);
  }

  #onMarkerClick(partner: Partner) {
    this.#panel.open(partner);
  }

  #renderConsentGate() {
    this.#root.setAttribute('data-state', 'consent');
    this.#content.innerHTML = `
      <div class="gate" role="region" aria-label="Service-Partner Karte">
        <div class="gate__bg" aria-hidden="true"></div>
        <div class="gate__body">
          <h3 class="gate__title">Service-Partner in Ihrer Nähe</h3>
          <p class="gate__text">
            Die Karte zeigt unsere autorisierten Service-Partner.
            Aktivieren Sie Marketing-Cookies, um Google Maps zu laden.
          </p>
          <button type="button" class="gate__cta" data-action="open-settings">
            Cookie-Einstellungen öffnen
          </button>
        </div>
      </div>
    `;
    const btn = this.#content.querySelector<HTMLButtonElement>('[data-action="open-settings"]');
    btn?.addEventListener('click', () => {
      const opened = openCookieSettings();
      if (!opened) {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
    });
  }

}
